/**
 * referralLinkManager.js
 * 
 * Gestiona el almacenamiento y recuperación de códigos de referidos de embajadores
 * en el LocalStorage del navegador. Implementa estrategia "last-click wins" por evento.
 */

const STORAGE_KEY = 'ambassador_referrals';
const EXPIRATION_DAYS = 30;

/**
 * Guarda un código de referido para un evento específico
 * @param {string} eventUid - UID del evento
 * @param {string} referralCode - Código del referido del embajador
 * @param {object} ambassadorInfo - Información del embajador (nombre, alias)
 */
export const saveReferralForEvent = (eventUid, referralCode, ambassadorInfo = {}) => {
    try {
        const referrals = getAllReferrals();
        
        // Guardar o actualizar el referral para este evento (last-click wins)
        referrals[eventUid] = {
            code: referralCode,
            ambassadorName: ambassadorInfo.nombre || null,
            ambassadorLastName: ambassadorInfo.apellido || null,
            ambassadorAlias: ambassadorInfo.alias || null,
            savedAt: new Date().toISOString(),
            expiresAt: new Date(Date.now() + EXPIRATION_DAYS * 24 * 60 * 60 * 1000).toISOString()
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(referrals));
        
        console.log(`[ReferralManager] Saved referral for event ${eventUid}:`, referrals[eventUid]);
        
        return true;
    } catch (error) {
        console.error('[ReferralManager] Error saving referral:', error);
        return false;
    }
};

/**
 * Obtiene el código de referido para un evento específico
 * @param {string} eventUid - UID del evento
 * @returns {object|null} Objeto con información del referido o null si no existe/expiró
 */
export const getReferralForEvent = (eventUid) => {
    try {
        const referrals = getAllReferrals();
        const referral = referrals[eventUid];

        if (!referral) {
            return null;
        }

        // Verificar si ha expirado
        const now = new Date();
        const expiresAt = new Date(referral.expiresAt);

        if (now > expiresAt) {
            console.log(`[ReferralManager] Referral for event ${eventUid} has expired`);
            removeReferralForEvent(eventUid);
            return null;
        }

        return {
            code: referral.code,
            ambassadorName: referral.ambassadorName,
            ambassadorLastName: referral.ambassadorLastName,
            ambassadorAlias: referral.ambassadorAlias,
            savedAt: referral.savedAt
        };
    } catch (error) {
        console.error('[ReferralManager] Error getting referral:', error);
        return null;
    }
};

/**
 * Elimina el referido de un evento específico
 * @param {string} eventUid - UID del evento
 */
export const removeReferralForEvent = (eventUid) => {
    try {
        const referrals = getAllReferrals();
        delete referrals[eventUid];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(referrals));
        console.log(`[ReferralManager] Removed referral for event ${eventUid}`);
    } catch (error) {
        console.error('[ReferralManager] Error removing referral:', error);
    }
};

/**
 * Obtiene todos los referidos almacenados
 * @returns {object} Objeto con todos los referidos indexados por eventUid
 */
const getAllReferrals = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch (error) {
        console.error('[ReferralManager] Error parsing stored referrals:', error);
        return {};
    }
};

/**
 * Limpia referidos expirados del LocalStorage
 */
export const cleanExpiredReferrals = () => {
    try {
        const referrals = getAllReferrals();
        const now = new Date();
        let cleaned = 0;

        Object.keys(referrals).forEach(eventUid => {
            const expiresAt = new Date(referrals[eventUid].expiresAt);
            if (now > expiresAt) {
                delete referrals[eventUid];
                cleaned++;
            }
        });

        if (cleaned > 0) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(referrals));
            console.log(`[ReferralManager] Cleaned ${cleaned} expired referral(s)`);
        }

        return cleaned;
    } catch (error) {
        console.error('[ReferralManager] Error cleaning expired referrals:', error);
        return 0;
    }
};

/**
 * Limpia todos los referidos del LocalStorage
 */
export const clearAllReferrals = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        console.log('[ReferralManager] Cleared all referrals');
    } catch (error) {
        console.error('[ReferralManager] Error clearing referrals:', error);
    }
};

/**
 * Obtiene estadísticas de los referidos almacenados
 * @returns {object} Estadísticas (total, expired, active)
 */
export const getReferralStats = () => {
    try {
        const referrals = getAllReferrals();
        const now = new Date();
        
        const total = Object.keys(referrals).length;
        let expired = 0;
        let active = 0;

        Object.values(referrals).forEach(referral => {
            const expiresAt = new Date(referral.expiresAt);
            if (now > expiresAt) {
                expired++;
            } else {
                active++;
            }
        });

        return { total, expired, active };
    } catch (error) {
        console.error('[ReferralManager] Error getting stats:', error);
        return { total: 0, expired: 0, active: 0 };
    }
};
